import { registerPlugin } from '@capacitor/core';
import { useEffect } from 'react';
import { isNativeApp } from '../native';

/**
 * Kleines eigenes Plugin der Hülle (`KeepAwakePlugin.java`): Die Android-WebView kennt die Wake-Lock-API nicht überall,
 * deshalb hält die Hülle den Bildschirm selbst wach. Eine ältere App ohne das Plugin wirft nur „nicht implementiert“.
 */
interface KeepAwakePlugin {
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}
export const KeepAwake = registerPlugin<KeepAwakePlugin>('KeepAwake');

/**
 * Hält den Bildschirm der Kasse wach, solange `active` gilt (Bildschirmsperre mitten im Verkauf stört). Ohne Unterstützung
 * oder bei Ablehnung passiert nichts. Der Browser gibt die Sperre beim Verlassen des Tabs frei, deshalb wird sie beim
 * Zurückkehren neu angefordert.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !isNativeApp()) return;
    void KeepAwake.enable().catch(() => undefined);
    return () => {
      void KeepAwake.disable().catch(() => undefined);
    };
  }, [active]);
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    const state: { sentinel: WakeLockSentinel | null; cancelled: boolean } = {
      sentinel: null,
      cancelled: false,
    };
    const request = async (): Promise<void> => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (state.cancelled) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        state.sentinel = sentinel;
      } catch {
        // abgelehnt (z. B. Stromsparmodus): die Kasse läuft normal weiter
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      state.cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void state.sentinel?.release().catch(() => undefined);
      state.sentinel = null;
    };
  }, [active]);
}
